import { useLayoutEffect, useSyncExternalStore, type RefObject } from 'react'

/* ---------------------------------------------------------------------------
   App-shell layout helpers
   ---------------------------------------------------------------------------
   The document scrolls (not an inner box), the page header is sticky, and at
   desktop widths the side menu is a persistent sidebar instead of a drawer.
   ------------------------------------------------------------------------ */

/** Width at which the side menu stops being a drawer. Mirrors ui.css. */
export const DESKTOP_NAV_QUERY = '(min-width: 1024px)'

/** Live `matchMedia` result; re-renders when the query flips. */
export function useMediaQuery (query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mql = window.matchMedia(query)
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    },
    () => window.matchMedia(query).matches,
    () => false
  )
}

/** True when the side menu is shown as a persistent sidebar. */
export function useIsDesktopNav (): boolean {
  return useMediaQuery(DESKTOP_NAV_QUERY)
}

const HEADER_HEIGHT_VAR = '--page-header-height'

/**
 * Publishes the routed page's sticky header height as `--page-header-height`
 * on <html>, so sticky strips inside the page (tab bars, side columns) can sit
 * under it and `scroll-padding-top` keeps anchors and scrollIntoView clear of
 * it. Only the first header inside `.app-main` counts; headers inside modals
 * and the side menu are ignored.
 */
export function usePublishHeaderHeight (ref: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const el = ref.current
    const main = el?.closest('.app-main')
    if (!el || !main || main.querySelector('.app-header') !== el) return
    const root = document.documentElement
    const publish = () => root.style.setProperty(HEADER_HEIGHT_VAR, `${el.offsetHeight}px`)
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(el)
    return () => {
      observer.disconnect()
      root.style.removeProperty(HEADER_HEIGHT_VAR)
    }
  }, [ref])
}
